import throttle from 'lodash.throttle'
import { store } from '../store'
import { checkIsAuthenticated } from '../lib/auth'
import {
  STORAGE_KEY_ACTIVE_SESSION_ID,
  STORAGE_KEY_ACTIVE_SESSION_TIMESTAMP,
  STORAGE_KEY_LAST_SESSION_ACTIVITY_TIME
} from '../storage-keys'
import { segmentTrackActiveSessionStarted, segmentTrackActiveSessionEnded } from './segment-track'
import { ACTIVE_SESSION_TTL_MS } from './constants'

// Check if the user is currently authenticated via the Redux store
const isAuthenticated = () => checkIsAuthenticated(store.getState())

/**
 * Get the current active session ID from localStorage.
 * Returns null if no active session exists.
 * Helper function to consistently retrieve the activeSessionId.
 * @returns {string|null} activeSessionId
 */
export const getActiveSessionId = () => {
  return localStorage.getItem(STORAGE_KEY_ACTIVE_SESSION_ID)
}

/**
 * Check if the current session is still within TTL
 * @returns {boolean} true if TTL not expired, false otherwise
 */
const isSessionWithinTTL = () => {
  const activeSessionId = getActiveSessionId()
  const lastSessionActivityTime = Number(localStorage.getItem(STORAGE_KEY_LAST_SESSION_ACTIVITY_TIME))

  if (!activeSessionId || !Number.isFinite(lastSessionActivityTime)) return false
  return Date.now() - lastSessionActivityTime <= ACTIVE_SESSION_TTL_MS
}

/**
 * Update the last session activity timestamp in localStorage.
 * - Throttled to run at most once every 5 seconds
 * - Prevents frequent synchronous localStorage writes on the main thread
 * - Millisecond level accuracy is not required for session activity tracking
 * @param {number} timestamp - current timestamp in milliseconds
 */
const throttledUpdateLastActivity = throttle((timestamp) => {
  localStorage.setItem(STORAGE_KEY_LAST_SESSION_ACTIVITY_TIME, timestamp)
}, 5000)

/**
 * Create a new session:
 * - Generates new activeSessionId
 * - Sets activeSessionTimestamp and lastSessionActivityTime
 * @returns {{activeSessionId: string, activeSessionTimestamp: number}}
 */
const createNewSession = () => {
  const newSessionId = crypto.randomUUID()
  const currentTimestamp = Date.now()

  localStorage.setItem(STORAGE_KEY_ACTIVE_SESSION_ID, newSessionId)
  localStorage.setItem(STORAGE_KEY_ACTIVE_SESSION_TIMESTAMP, currentTimestamp)
  // Intentionally not throttled: session start time must be persisted immediately to ensure correct TTL validation
  localStorage.setItem(STORAGE_KEY_LAST_SESSION_ACTIVITY_TIME, currentTimestamp)

  return { activeSessionId: newSessionId, activeSessionTimestamp: currentTimestamp }
}

/**
 * Capture session start
 * - Can be called on page load or on user login/signup
 * - Creates a new session or resumes an existing one if within TTL
 * - Updates last activity timestamp (throttled)
 * - Sends TRACK_ACTIVE_SESSION_STARTED event
 * - Accepts a userProfile object for signin/signup because analytics.instance.user().traits().profile may not be initialized yet
 * @param {object} userProfile - optional profile info to attach during login/signup
 * @returns {string} activeSessionId of the started or resumed session
 */
export const captureSessionStart = (userProfile = {}) => {
  const currentTimestamp = Date.now()
  let activeSessionId = getActiveSessionId()
  let sessionResumed = false

  if (!activeSessionId || !isSessionWithinTTL()) {
    const newSession = createNewSession()
    activeSessionId = newSession.activeSessionId
    sessionResumed = false
  } else {
    sessionResumed = true
    // Throttled update of last session activity
    throttledUpdateLastActivity(currentTimestamp)
  }

  segmentTrackActiveSessionStarted({ activeSessionId, sessionResumed, ...userProfile })

  return activeSessionId
}

/**
 * Capture session end
 * - Only runs if the user is authenticated
 * - Calculates the session duration in seconds: TTL capped for backgrounded sessions, actual time for terminated sessions
 * - Sends a Segment event 'Active Session Ended'
 * - Does not remove session data: session can be resumed on next page load
 * @param {string} endReason - reason for session ending ('backgrounded' or 'terminated')
 */
const captureSessionEnd = (endReason) => {
  if (!isAuthenticated()) return

  const activeSessionId = getActiveSessionId()
  const activeSessionTimestamp = Number(localStorage.getItem(STORAGE_KEY_ACTIVE_SESSION_TIMESTAMP))
  const lastSessionActivityTime = Number(localStorage.getItem(STORAGE_KEY_LAST_SESSION_ACTIVITY_TIME))

  // Validate session exists
  if (!activeSessionId || !activeSessionTimestamp || (!lastSessionActivityTime && endReason === 'backgrounded')) return

  // Default end time is now
  let endTime = Date.now()
  if (endReason === 'backgrounded') {
    // TTL capped end time
    endTime = Math.min(lastSessionActivityTime + ACTIVE_SESSION_TTL_MS, endTime)
  }

  // Ensure duration is never negative (e.g. system clock changes or corrupted storage)
  const durationInSeconds = Math.max(
    0,
    Math.floor((endTime - activeSessionTimestamp) / 1000)
  )

  segmentTrackActiveSessionEnded({ activeSessionId, durationInSeconds, endReason })
}

/**
 * Refresh last session activity with TTL check:
 * - Only runs if the user is authenticated
 * - If TTL expired, send end event (backgrounded) and start a new session
 * - If TTL valid, just update last session activity timestamp
 * @returns {string|null} current activeSessionId after refresh
 */
export const refreshSessionActivityWithTTLCheck = () => {
  if (!isAuthenticated()) return null

  const currentTimestamp = Date.now()
  let activeSessionId = getActiveSessionId()

  if (!activeSessionId) return null

  // TTL expired
  if (!isSessionWithinTTL()) {
    // Send end event (backgrounded)
    captureSessionEnd('backgrounded')

    // Start new session
    const newSession = createNewSession()
    activeSessionId = newSession.activeSessionId
    segmentTrackActiveSessionStarted({ activeSessionId, sessionResumed: false })
  } else {
    // Throttled update of last session activity
    throttledUpdateLastActivity(currentTimestamp)
  }

  return activeSessionId
}

/**
 * Event listeners to trigger session start and end
 */
// Start session on page load, only if the user is authenticated
window.addEventListener('load', () => {
  if (!isAuthenticated()) return
  captureSessionStart()
})

// End session (terminated) when browser tab is closing, refreshing, or navigating away
window.addEventListener('beforeunload', () => captureSessionEnd('terminated'))
